import React, { useCallback, useState, forwardRef, ForwardedRef } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import messages from './messages';
import chargeAfterService from '../../services/chargeAfter';
import PrimaryButton from '../common/PrimaryButton';
import { ChargeAfterButtonProps } from './types';
import { CartInStore } from '../../slices/cart/types';

const ChargeAfterButton = forwardRef(
  (
    { onSuccess, onFailure, isLoading }: ChargeAfterButtonProps,
    ref: ForwardedRef<HTMLButtonElement | null>,
  ) => {
    const [chargeAfterModalInProgress, setChargeAfterModalInProgress] =
      useState(false);
    const dispatch = useDispatch();
    const { formatMessage, locale } = useIntl();

    const cart = useSelector((state) => (state as { cart: CartInStore }).cart);

    const buttonTitle = cart.isDraft
      ? formatMessage(messages.continueToPurpleFinancing)
      : formatMessage(messages.payWithPurpleCreditCard);

    const handleOnClick = useCallback(async () => {
      setChargeAfterModalInProgress(true);
      dispatch({ type: 'setPaymentStatus', status: 'loading' });
      const chargeAfterObject = chargeAfterService.buildChargeAfterObject({
        cart,
        locale,
        callback: (token: string, data: unknown, error: unknown) => {
          setChargeAfterModalInProgress(false);
          if (!error) {
            onSuccess({ token, data, error });
          } else {
            dispatch({ type: 'setPaymentStatus', status: 'failed' });
            dispatch({
              type: 'setStatus',
              status: 'idle',
            });
            onFailure(JSON.stringify({ token, data, error }));
          }
        },
        onDataUpdate: (
          updatedData: unknown,
          callback: (data: unknown) => void,
        ) => {
          // TODO: returning an empty object to avoid blocking the modal in some cases,
          // this object will be populated when the ticket is done
          callback({});
        },
      });
      if (window.ChargeAfter && chargeAfterObject) {
        await window.ChargeAfter.payments.present(
          'checkout',
          chargeAfterObject,
        );
      }
    }, [cart, dispatch, locale, onFailure, onSuccess]);

    return (
      <PrimaryButton
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onClick={handleOnClick}
        isLoading={isLoading || chargeAfterModalInProgress}
        title={buttonTitle}
        id="chargeafter-button"
        ref={ref}
      />
    );
  },
);

export default ChargeAfterButton;
